<template>
  <section class="pad-t-0 edit-promo-page">
    <div class="bb-container">
      <div class="back">
        <div class="back__back">
          <a @click.prevent="$router.back()">
            <div class="back__name">
              <img
                src="../../assets/img/ico/ico-arrow-back.svg"
                loading="lazy"
                alt="Volver"
                class="back__arrow"
              />
            </div>
          </a>
        </div>
      </div>

      <div class="g-edit" v-show="loaded">
        <div class="g-edit__header">
          <h1 class="g-edit__title">
            {{ isEdit ? "Editar promoción afiliada" : "Nueva promoción afiliada" }}
          </h1>
        </div>

        <div class="g-edit__cols">
          <div class="g-edit__content">
            <h3 class="g-edit__head header-4">Datos básicos</h3>

            <div class="edit-data">
              <div class="edit-data__item">
                <div class="bb-form-item">
                  <div class="bb-form-label">Título *</div>
                  <input
                    v-model="form.title"
                    class="bb-form"
                    type="text"
                    maxlength="300"
                    @blur="touch('title')"
                  />
                  <div class="bb-form-error" v-show="hasError('title')">
                    Campo obligatorio
                  </div>
                </div>
              </div>

              <div class="edit-data__item">
                <div class="bb-form-item">
                  <div class="bb-form-label">Descripción</div>
                  <textarea
                    v-model="form.description"
                    class="bb-form-textarea"
                    rows="4"
                  />
                </div>
              </div>

              <div class="edit-data__item">
                <div class="bb-form-item">
                  <div class="bb-form-label">Mercado (ISO2) *</div>
                  <select v-model="form.marketCountryIso2" class="bb-form" @blur="touch('marketCountryIso2')">
                    <option disabled value="">Selecciona...</option>
                    <option v-for="m in markets" :key="m" :value="m">{{ m }}</option>
                  </select>
                  <div class="bb-form-error" v-show="hasError('marketCountryIso2')">
                    Campo obligatorio
                  </div>
                </div>
              </div>

              <div class="edit-data__item">
                <div class="bb-form-item">
                  <div class="bb-form-label">Link (landing)</div>
                  <div class="bb-form-group">
                    <input
                      v-model="form.linkUrl"
                      class="bb-form"
                      type="text"
                      maxlength="800"
                      placeholder="https://..."
                      @input="onLinkChange"
                      @keypress="preventWhitespace"
                      @blur="touch('linkUrl')"
                    />
                    <a
                      v-if="isValidUrl(form.linkUrl)"
                      :href="form.linkUrl"
                      target="_blank"
                      class="bb-form-group__ico"
                      rel="noopener"
                    >
                      <img src="../../assets/img/ico/ico-external-link.svg" alt="" />
                    </a>
                  </div>
                  <div class="bb-form-error" v-show="hasError('linkUrl')">
                    La URL no puede contener espacios en blanco
                  </div>
                </div>
              </div>

              <!-- Retailer Selector -->
              <div class="edit-data__item">
                <div class="bb-form-item">
                  <div class="bb-form-label">Tienda (web o dominio) *</div>
                  
                  <!-- Input siempre visible y editable -->
                  <input
                    v-model="form.retailerName"
                    class="bb-form"
                    type="text"
                    maxlength="200"
                    placeholder="Ej: https://www.nike.com o simplemente nike"
                    @input="onRetailerNameChange"
                    @blur="touch('retailerName')"
                    @keypress="onRetailerKeyPress"
                  />
                  
                  <!-- Card de estado: Retailer encontrado -->
                  <div v-if="retailerFound" class="retailer-selector">
                    <div class="retailer-selector__found">
                      <div class="retailer-selector__status">
                        <span class="retailer-selector__icon">✓</span>
                        <span>Retailer detectado</span>
                      </div>
                      <div class="retailer-selector__info">
                        <div class="retailer-selector__logo">
                          <img 
                            v-if="selectedRetailer.logoUrl" 
                            :src="'https://images.hellobb.net/logos/' + selectedRetailer.logoUrl" 
                            :alt="selectedRetailer.name"
                            class="retailer-selector__logo-img"
                          />
                          <span v-else>{{ selectedRetailerLogo }}</span>
                        </div>
                        <div class="retailer-selector__details">
                          <div class="retailer-selector__name">{{ selectedRetailer.name }}</div>
                          <div class="retailer-selector__key">{{ selectedRetailer.internalKey }}</div>
                        </div>
                      </div>
                      <div class="retailer-selector__actions">
                        <button
                          type="button"
                          class="button button--secondary button--small"
                          @click="openEditRetailerModal"
                        >
                          Editar
                        </button>
                      </div>
                    </div>
                  </div>

                  <!-- Card de estado: Retailer NO encontrado -->
                  <div v-else-if="form.retailerName && !hasError('retailerName')" class="retailer-selector">
                    <div class="retailer-selector__not-found">
                      <div class="retailer-selector__status retailer-selector__status--warning">
                        <span class="retailer-selector__icon">⚠</span>
                        <span>Retailer no encontrado</span>
                      </div>
                      <div class="retailer-selector__suggested">
                        Dominio: <strong>{{ form.retailerName }}</strong>
                      </div>
                      <div class="retailer-selector__actions mar-t-1">
                        <button
                          type="button"
                          class="button button--primary button--small"
                          @click="openCreateRetailerModal"
                        >
                          Crear retailer
                        </button>
                      </div>
                    </div>
                  </div>

                  <div class="bb-form-error" v-show="hasError('retailerName')">
                    {{ errorRetailer }}
                  </div>
                </div>
              </div>

              <div class="edit-data__item">
                <div class="bb-form-item">
                  <div class="bb-form-label">Red afiliación</div>
                  <select v-model="form.affiliateNetwork" class="bb-form">
                    <option value="">(vacío)</option>
                    <option v-for="n in affiliateNetworks" :key="n" :value="n">{{ n }}</option>
                  </select>
                </div>
              </div>
            </div>

            <h3 class="g-edit__head header-4 mar-t-2">Cupón</h3>

            <div class="edit-data">
              <!-- <div class="edit-data__item">
                <div class="bb-form-item">
                  <div class="bb-form-label">Tipo cupón</div>
                  <select v-model="form.couponType" class="bb-form">
                    <option value="code">Código</option>
                    <option value="none">Sin cupón</option>
                  </select>
                </div>
              </div> -->

              <div class="edit-data__item">
                <div class="bb-form-item">
                  <div class="bb-form-label">Código cupón</div>
                  <input
                    v-model="form.couponCode"
                    class="bb-form"
                    type="text"
                    maxlength="100"
                    placeholder="Ej: VERANO20"
                  />
                </div>
              </div>
            </div>

            <h3 class="g-edit__head header-4 mar-t-2" v-show="false">Alcance y mecánica</h3>

            <div class="edit-data" v-show="false">
              <div class="edit-data__item">
                <div class="bb-form-item">
                  <div class="bb-form-label">Scope</div>
                  <select v-model="form.scope" class="bb-form">
                    <option value="retailer">Toda la tienda</option>
                    <option value="product_group">Grupo de productos</option>
                    <option value="conditional">Condiciones especiales</option>
                  </select>
                </div>
              </div>

              <div class="edit-data__item">
                <div class="bb-form-item">
                  <div class="bb-form-label">Tipo de promo</div>
                  <select v-model="form.promotionType" class="bb-form">
                    <option value="unknown">Desconocido</option>
                    <option value="percentage">Porcentaje</option>
                    <option value="fixed_amount">Importe fijo</option>
                    <option value="free_gift">Regalo</option>
                    <option value="shipping">Envío</option>
                    <option value="sale">Rebajas</option>
                  </select>
                </div>
              </div>

              <div class="edit-data__item" v-if="form.promotionType === 'percentage'">
                <div class="bb-form-item">
                  <div class="bb-form-label">% descuento</div>
                  <input v-model.number="form.discountPercent" class="bb-form" type="number" step="0.01" min="0" max="100" />
                </div>
              </div>

              <div class="edit-data__item" v-if="form.promotionType === 'fixed_amount'">
                <div class="bb-form-item">
                  <div class="bb-form-label">Descuento (importe)</div>
                  <input v-model.number="form.discountAmount" class="bb-form" type="number" step="0.01" min="0" />
                </div>
              </div>

              <div class="edit-data__item" v-if="form.promotionType === 'free_gift'">
                <div class="bb-form-item">
                  <div class="bb-form-label">Descripción del regalo</div>
                  <input v-model="form.freeGiftDescription" class="bb-form" type="text" maxlength="500" />
                </div>
              </div>

              <div class="edit-data__item" v-if="form.promotionType === 'shipping'">
                <div class="bb-form-item">
                  <div class="bb-form-label">Descripción del envío</div>
                  <input v-model="form.shippingDescription" class="bb-form" type="text" maxlength="500" />
                </div>
              </div>
            </div>

            <h3 class="g-edit__head header-4 mar-t-2" v-show="false">Condiciones</h3>

            <div class="edit-data" v-show="false">
              <div class="edit-data__item">
                <div class="bb-form-item">
                  <div class="bb-form-label">Pedido mínimo</div>
                  <input v-model.number="form.minOrderValue" class="bb-form" type="number" step="0.01" min="0" />
                </div>
              </div>

              <div class="edit-data__item">
                <div class="bb-form-item">
                  <div class="bb-form-label">Cantidad mínima</div>
                  <input v-model.number="form.minQuantity" class="bb-form" type="number" step="1" min="0" />
                </div>
              </div>
            </div>

            <h3 class="g-edit__head header-4 mar-t-2">Vigencia y estado</h3>

            <div class="edit-data">
              <div class="edit-data__item">
                <div class="bb-form-item">
                  <div class="bb-form-label">Inicio</div>
                  <input v-model="form.startAt" class="bb-form" type="date" />
                </div>
              </div>

              <div class="edit-data__item">
                <div class="bb-form-item">
                  <div class="bb-form-label">Fin</div>
                  <input v-model="form.endAt" class="bb-form" type="date" />
                </div>
              </div>

              <div class="edit-data__item">
                <div class="bb-form-item">
                  <div class="bb-form-label">Estado</div>
                  <select v-model="form.status" class="bb-form">
                    <option value="active">Activa</option>
                    <option value="disabled">Desactivada</option>
                    <option value="deleted">Eliminada</option>
                  </select>
                </div>
              </div>

              <div class="edit-data__item">
                <div class="bb-form-item">
                  <div class="bb-form-label">Notas internas</div>
                  <textarea v-model="form.notesInternal" class="bb-form-textarea" rows="3" />
                </div>
              </div>
            </div>

            <div class="g-edit__actions-column">
              <loading-button v-if="saving"></loading-button>

              <button
                v-else
                class="button button--primary button--block"
                @click.prevent="save"
                :disabled="disabled"
              >
                Guardar
              </button>

              <a @click.prevent="$router.back()">
                <button class="button button--secondary button--block">Cancelar</button>
              </a>
            </div>
          </div>

          <div class="g-edit__picture">
            <h3 class="g-edit__head header-4">Previsualización</h3>

            <div class="toast toast--aqua mar-b-1">
              <div class="toast__content">
                <div>
                  <strong>{{ selectedRetailer ? selectedRetailer.name : (form.retailerName || "Retailer") }}</strong> 
                  ({{ form.marketCountryIso2 || "XX" }})
                </div>
                <div class="mar-t-1">{{ form.title || "Título de la promoción" }}</div>
                <div v-if="form.couponCode" class="mar-t-1">
                  <strong>Código:</strong> {{ form.couponCode }}
                </div>
                <div v-if="form.promotionType === 'percentage' && form.discountPercent != null" class="mar-t-1">
                  <strong>-{{ form.discountPercent }}%</strong>
                </div>
                <div v-if="form.promotionType === 'fixed_amount' && form.discountAmount != null" class="mar-t-1">
                  <strong>-{{ form.discountAmount }}</strong>
                </div>
                <div v-if="form.minOrderValue != null" class="mar-t-1">
                  <span>Pedido mínimo: {{ form.minOrderValue }}</span>
                </div>
                <div v-if="isValidUrl(form.linkUrl)" class="mar-t-1">
                  <a :href="form.linkUrl" target="_blank" rel="noopener">Ver oferta</a>
                </div>
              </div>
            </div>

            <div v-if="errorMsg" class="bb-form-error">
              {{ errorMsg }}
            </div>
          </div>
        </div>
      </div>

      <div v-if="!loaded" class="mar-t-2">
        <loading-button></loading-button>
      </div>
    </div>

    <!-- Modal Crear Retailer -->
    <modal-dialog
      :show="showCreateRetailerModal"
      @close="closeCreateRetailerModal"
      target-class="modal__card--medium"
    >
      <div class="create-retailer-modal">
        <h3 class="header-3" style="margin-bottom: 24px;">
          {{ isEditingRetailer ? "Editar retailer" : "Crear nuevo retailer" }}
        </h3>
        
        <div class="edit-data">
          <div class="edit-data__item">
            <div class="bb-form-item">
              <div class="bb-form-label">Nombre *</div>
              <input
                v-model="newRetailer.name"
                class="bb-form"
                type="text"
                maxlength="200"
                placeholder="Ej: Nike, El Corte Inglés"
                @input="touch('newRetailerName')"
              />
              <div class="bb-form-error" v-show="hasError('newRetailerName')">
                Campo obligatorio
              </div>
            </div>
          </div>

          <div class="edit-data__item">
            <div class="bb-form-item">
              <div class="bb-form-label">Dominio (internalKey)</div>
              <input
                v-model="newRetailer.internalKey"
                class="bb-form"
                type="text"
                readonly
                style="background: #f8f9fa; cursor: not-allowed;"
              />
              <div style="font-size: 0.875rem; color: #6c757d; margin-top: 0.25rem;">
                {{ isEditingRetailer ? "El dominio no se puede modificar" : "Este campo se detectó automáticamente del enlace" }}
              </div>
            </div>
          </div>

          <div class="edit-data__item">
            <div class="bb-form-item">
              <div class="bb-form-label">Logo (opcional)</div>
              
              <!-- Logo actual (solo al editar) -->
              <div v-if="isEditingRetailer && currentRetailerLogoUrl && !newRetailer.logoPreview" style="margin-bottom: 12px;">
                <div style="font-size: 0.875rem; color: #6c757d; margin-bottom: 8px;">Logo actual:</div>
                <img
                  :src="currentRetailerLogoUrl"
                  alt="Logo actual"
                  style="max-width: 120px; max-height: 120px; border: 1px solid #dee2e6; border-radius: 4px;"
                />
              </div>

              <input
                ref="logoInput"
                type="file"
                accept="image/*"
                @change="onLogoChange"
                class="bb-form"
                style="padding: 8px;"
              />
              
              <!-- Preview del nuevo logo -->
              <div v-if="newRetailer.logoPreview" style="margin-top: 12px;">
                <div style="font-size: 0.875rem; color: #6c757d; margin-bottom: 8px;">
                  {{ isEditingRetailer ? "Nuevo logo:" : "Preview:" }}
                </div>
                <img
                  :src="newRetailer.logoPreview"
                  alt="Preview"
                  style="max-width: 120px; max-height: 120px; border: 1px solid #dee2e6; border-radius: 4px;"
                />
                <button
                  type="button"
                  @click="clearLogo"
                  class="button button--link button--small"
                  style="display: block; margin-top: 8px;"
                >
                  {{ isEditingRetailer ? "Cancelar cambio de logo" : "Eliminar logo" }}
                </button>
              </div>
            </div>
          </div>
        </div>

        <div style="display: flex; gap: 12px; margin-top: 24px; justify-content: flex-end;">
          <button
            type="button"
            @click="closeCreateRetailerModal"
            class="button button--secondary"
          >
            Cancelar
          </button>
          <loading-button v-if="creatingRetailer"></loading-button>
          <button
            v-else
            type="button"
            @click="isEditingRetailer ? updateRetailer() : createRetailer()"
            class="button button--primary"
            :disabled="!newRetailer.name"
          >
            {{ isEditingRetailer ? "Guardar cambios" : "Crear retailer" }}
          </button>
        </div>

        <div v-if="createRetailerError" class="bb-form-error" style="margin-top: 16px;">
          {{ createRetailerError }}
        </div>
      </div>
    </modal-dialog>
  </section>
</template>

<script>
import LoadingButton from "../../components/LoadingButton";
import ModalDialog from "../../components/ui/ModalDialog";
import { mapGetters } from "vuex";
import axios from "axios";

const API_BASE = process.env.API;

export default {
  name: "AdminAffiliatePromotionEditPage",
  components: { LoadingButton, ModalDialog },
  data() {
    return {
      loaded: false,
      saving: false,
      errorMsg: null,
      errorRetailer: null,
      touched: {},
      markets: ["ES", "FR", "IT", "PT", "DE", "NL", "BR", "MX"],
      affiliateNetworks: ["awin", "tradedoubler", "skimlinks", "effinity", "adtraction"],
      form: {
        title: "",
        description: "",
        marketCountryIso2: "",
        retailerName: "",
        affiliateNetwork: "",
        couponCode: "",
        couponType: "code",
        linkUrl: "",
        scope: "retailer",
        promotionType: "unknown",
        discountPercent: null,
        discountAmount: null,
        freeGiftDescription: "",
        shippingDescription: "",
        minOrderValue: null,
        minQuantity: null,
        newCustomersOnly: false,
        requiresLoyaltyCard: false,
        productGroupText: "",
        keywords: "",
        appliesToListTypes: "any",
        startAt: "",
        endAt: "",
        status: "active",
        notesInternal: "",
      },
      retailers: [],
      selectedRetailer: null,
      showCreateRetailerModal: false,
      creatingRetailer: false,
      createRetailerError: null,
      isEditingRetailer: false,
      editingRetailerId: null,
      newRetailer: {
        name: "",
        internalKey: "",
        logoUrl: null,
        logoPreview: null,
      },
    };
  },
  computed: {
    ...mapGetters({ auth_token: "auth_token" }),
    isEdit() {
      return !!this.$route.params.id;
    },
    disabled() {
      return (
        this.saving ||
        !this.form.title ||
        !this.form.marketCountryIso2 ||
        !this.form.retailerName ||
        this.hasWhitespace(this.form.retailerName) ||
        (this.form.linkUrl && this.hasWhitespace(this.form.linkUrl))
      );
    },
    retailerFound() {
      return this.selectedRetailer != null;
    },
    selectedRetailerLogo() {
      if (!this.selectedRetailer) return null;
      return this.getRetailerLogo(this.selectedRetailer);
    },
    currentRetailerLogoUrl() {
      if (!this.isEditingRetailer || !this.selectedRetailer || !this.selectedRetailer.logoUrl) {
        return null;
      }
      return "https://images.hellobb.net/logos/" + this.selectedRetailer.logoUrl;
    },
  },
  methods: {
    touch(field) {
      this.$set(this.touched, field, true);
    },

    hasWhitespace(value) {
      return value && /\s/.test(value);
    },

    hasError(field) {
      if (!this.touched[field]) return false;
      if (field === "title") return !this.form.title;
      if (field === "marketCountryIso2") return !this.form.marketCountryIso2;
      if (field === "retailerName") {
        //return !this.form.retailerName || this.hasWhitespace(this.form.retailerName);
        if (!this.form.retailerName) {
          this.errorRetailer = "Campo obligatorio";
          return true;
        }
        if (this.hasWhitespace(this.form.retailerName)) {
          this.errorRetailer = "Este campo no puede contener espacios en blanco ya que debe ser el dominio de la tienda";
          return true;
        }
      }
      if (field === "linkUrl") {
        return this.form.linkUrl && this.hasWhitespace(this.form.linkUrl);
      }
      if (field === "newRetailerName") return !this.newRetailer.name;
      return false;
    },

    isValidUrl(value) {
      if (!value) return false;
      try {
        new URL(value);
        return true;
      } catch {
        return false;
      }
    },

    preventWhitespace(event) {
      if (event.key === ' ') {
        event.preventDefault();
      }
    },

    onRetailerKeyPress(event) {
      const allowedControlKeys = [
        "Backspace",
        "Delete",
        "ArrowLeft",
        "ArrowRight",
        "Tab"
      ];

      if (allowedControlKeys.includes(event.key)) {
        return;
      }

      if (event.key === ' ') {
        event.preventDefault();
        return;
      }

      // Detectar SOLO letras mayúsculas
      if (/^[A-Z]$/.test(event.key)) {
        event.preventDefault();

        const start = event.target.selectionStart;
        const end = event.target.selectionEnd;

        const value = event.target.value;
        const newValue =
          value.slice(0, start) +
          event.key.toLowerCase() +
          value.slice(end);

        event.target.value = newValue;
        event.target.setSelectionRange(start + 1, start + 1);

        this.onRetailerNameChange({ target: event.target });
        return;
      }

      // Permitir solo a-z, 0-9 y -
      if (!/^[a-z0-9-]$/.test(event.key)) {
        event.preventDefault();
      }
    },

    getRetailerNameError() {
      if (!this.form.retailerName) {
        return "Campo obligatorio";
      }
      if (this.hasWhitespace(this.form.retailerName)) {
        return "El dominio no puede contener espacios en blanco";
      }
      return "";
    },

    getRetailerLogo(retailer) {
      if (retailer.logoUrl) {
        return retailer.logoUrl;
      }
      const words = retailer.name.split(' ');
      if (words.length >= 2) {
        return words[0][0].toUpperCase() + words[1][0].toUpperCase();
      }
      return retailer.name.substring(0, 2).toUpperCase();
    },
    selectRetailer(retailer) {
      this.selectedRetailer = retailer;
      this.form.retailerName = retailer.internalKey;
    },
    clearRetailerSelection() {
      this.selectedRetailer = null;
      this.form.retailerName = "";
    },
    openCreateRetailerModal() {
      this.isEditingRetailer = false;
      this.editingRetailerId = null;
      this.newRetailer = {
        name: "",
        internalKey: this.form.retailerName,
        logoUrl: null,
        logoPreview: null,
      };
      this.createRetailerError = null;
      this.showCreateRetailerModal = true;
    },
    openEditRetailerModal() {
      if (!this.selectedRetailer) return;
      
      this.isEditingRetailer = true;
      this.editingRetailerId = this.selectedRetailer.id;
      this.newRetailer = {
        name: this.selectedRetailer.name,
        internalKey: this.selectedRetailer.internalKey,
        logoUrl: null, // Solo cambiará si se sube una nueva
        logoPreview: null,
      };
      this.createRetailerError = null;
      this.showCreateRetailerModal = true;
    },
    closeCreateRetailerModal() {
      this.showCreateRetailerModal = false;
      this.isEditingRetailer = false;
      this.editingRetailerId = null;
      this.newRetailer = {
        name: "",
        internalKey: "",
        logoUrl: null,
        logoPreview: null,
      };
      this.createRetailerError = null;
      if (this.$refs.logoInput) {
        this.$refs.logoInput.value = "";
      }
    },
    onLogoChange(event) {
      const file = event.target.files[0];
      if (!file) return;

      if (!file.type.startsWith('image/')) {
        this.createRetailerError = "Por favor selecciona una imagen válida";
        return;
      }

      if (file.size > 2 * 1024 * 1024) {
        this.createRetailerError = "La imagen no puede superar los 2MB";
        return;
      }

      const reader = new FileReader();
      reader.onload = (e) => {
        this.newRetailer.logoUrl = e.target.result;
        this.newRetailer.logoPreview = e.target.result;
        this.createRetailerError = null;
      };
      reader.onerror = () => {
        this.createRetailerError = "Error al leer la imagen";
      };
      reader.readAsDataURL(file);
    },
    clearLogo() {
      this.newRetailer.logoUrl = null;
      this.newRetailer.logoPreview = null;
      if (this.$refs.logoInput) {
        this.$refs.logoInput.value = "";
      }
    },
    async createRetailer() {
      this.createRetailerError = null;
      this.touch('newRetailerName');

      if (!this.newRetailer.name) {
        this.createRetailerError = "El nombre es obligatorio";
        return;
      }

      this.creatingRetailer = true;

      try {
        const payload = {
          name: this.newRetailer.name,
          internalKey: this.newRetailer.internalKey,
          logoUrl: this.newRetailer.logoUrl,
        };

        const res = await axios.post(`${API_BASE}/retailers`, payload, {
          headers: { Authorization: `Bearer ${this.auth_token}` }
        });

        const createdRetailer = res.data;
        this.retailers.push(createdRetailer);
        this.selectRetailer(createdRetailer);
        this.closeCreateRetailerModal();
      } catch (e) {
        console.error("Error creating retailer:", e);
        this.createRetailerError = e.response?.data?.message || "No se pudo crear el retailer. Intenta de nuevo.";
      } finally {
        this.creatingRetailer = false;
      }
    },
    async updateRetailer() {
      this.createRetailerError = null;
      this.touch('newRetailerName');

      if (!this.newRetailer.name) {
        this.createRetailerError = "El nombre es obligatorio";
        return;
      }

      this.creatingRetailer = true;

      try {
        const payload = {
          name: this.newRetailer.name,
          // Solo incluir logoUrl si se subió una nueva imagen
          ...(this.newRetailer.logoUrl && { logoUrl: this.newRetailer.logoUrl }),
        };

        const res = await axios.put(`${API_BASE}/retailers/${this.editingRetailerId}`, payload, {
          headers: { Authorization: `Bearer ${this.auth_token}` }
        });

        const updatedRetailer = res.data;
        
        // Actualizar en la lista local
        const index = this.retailers.findIndex(r => r.id === this.editingRetailerId);
        if (index !== -1) {
          this.$set(this.retailers, index, updatedRetailer);
        }
        
        // Actualizar el selectedRetailer
        this.selectedRetailer = updatedRetailer;
        
        this.closeCreateRetailerModal();
      } catch (e) {
        console.error("Error updating retailer:", e);
        this.createRetailerError = e.response?.data?.message || "No se pudo actualizar el retailer. Intenta de nuevo.";
      } finally {
        this.creatingRetailer = false;
      }
    },
    extractRetailerFromUrl(url) {
      if (!url) return null;
      
      try {
        const urlObj = new URL(url);
        let hostname = urlObj.hostname.replace(/^www\./, "");
        
        const affiliateNetworks = [
          "awin1.com", "awin.com", "tradedoubler.com", "go.skimresources.com",
          "skimlinks.com", "effiliation.com", "adtraction.com", "shareasale.com",
          "cj.com", "dpbolvw.net", "anrdoezrs.net", "jdoqocy.com", "go.adt291.com"
        ];
        
        if (affiliateNetworks.some(network => hostname.includes(network))) {
          let foundUrl = false;
          const urlParams = ["ued", "url", "u", "p", "murl", "destination", "goto", "target"];
          
          for (const paramName of urlParams) {
            const paramValue = urlObj.searchParams.get(paramName);
            if (paramValue) {
              try {
                const decodedUrl = decodeURIComponent(paramValue);
                const targetUrl = new URL(decodedUrl);
                hostname = targetUrl.hostname.replace(/^www\./, "");
                foundUrl = true;
                break;
              } catch {
                const match = paramValue.match(/(?:https?:\/\/)?(?:www\.)?([^\/\?\&\s]+)/i);
                if (match && match[1]) {
                  hostname = match[1];
                  foundUrl = true;
                  break;
                }
              }
            }
          }
          
          if (!foundUrl) {
            const fullUrl = url.toString();
            const encodedUrlMatch = fullUrl.match(/https?%3A%2F%2F[^&\s]+/i);
            if (encodedUrlMatch) {
              try {
                const decodedUrl = decodeURIComponent(encodedUrlMatch[0]);
                const targetUrl = new URL(decodedUrl);
                hostname = targetUrl.hostname.replace(/^www\./, "");
                foundUrl = true;
              } catch {}
            }
            
            if (!foundUrl) {
              const domainMatch = fullUrl.match(/([a-z0-9-]+\.[a-z]{2,}(?:\.[a-z]{2,})?)/gi);
              if (domainMatch) {
                const filtered = domainMatch.filter(d => 
                  !affiliateNetworks.some(network => d.includes(network)) && d !== hostname
                );
                if (filtered.length > 0) {
                  hostname = filtered[0].replace(/^www\./, "");
                }
              }
            }
          }
        }
        
        const parts = hostname.split(".");
        if (parts.length >= 2) {
          return parts[parts.length - 2];
        }
        return hostname;
      } catch {
        return null;
      }
    },
    onLinkChange() {
      const domain = this.extractRetailerFromUrl(this.form.linkUrl);
      if (domain) {
        this.form.retailerName = domain;
        this.searchRetailerByName(domain);
      }
    },
    onRetailerNameChange() {
      let value = this.form.retailerName.trim();
      
      if (!value) {
        this.selectedRetailer = null;
        return;
      }
      
      // Si parece una URL, extraer el dominio
      if (value.startsWith('http') || value.includes('www.') || value.includes('.com') || value.includes('.es') || value.includes('.fr') || value.includes('.it')) {
        const domain = this.extractRetailerFromUrl(value);
        if (domain) {
          this.form.retailerName = domain; // Auto-limpia a dominio corto
          value = domain;
        }
      }
      
      this.searchRetailerByName(value);
    },
    searchRetailerByName(name) {
      if (!name) {
        this.selectedRetailer = null;
        return;
      }
      
      const found = this.retailers.find(r => 
        r.internalKey?.toLowerCase() === name.toLowerCase()
      );
      
      if (found) {
        this.selectedRetailer = found;
      } else {
        this.selectedRetailer = null;
      }
    },
    toDateInputValue(iso) {
      if (!iso) return "";
      return String(iso).substring(0, 10);
    },
    fromDateStart(value) {
      if (!value) return null;
      const [y, m, d] = value.split("-").map(Number);
      return new Date(Date.UTC(y, m - 1, d, 0, 0, 0, 0)).toISOString();
    },
    fromDateEnd(value) {
      if (!value) return null;
      const [y, m, d] = value.split("-").map(Number);
      return new Date(y, m - 1, d, 23, 59, 59, 999).toISOString();
    },
    async loadRetailers() {
      try {
        const res = await this.$store.dispatch("getRetailers");
        this.retailers = (res || []).filter(r => r.internalKey);
      } catch (e) {
        console.log("No se pudieron cargar los retailers", e);
        this.retailers = [];
      }
    },
    async load() {
      if (!this.isEdit) return;

      try {
        const id = this.$route.params.id;
        const res = await axios.get(`${API_BASE}/admin/affiliate-promotions/${id}`, {
          headers: { Authorization: `Bearer ${this.auth_token}` }
        });
        const p = res.data;

        Object.keys(this.form).forEach(key => {
          if (key === "startAt" || key === "endAt") {
            this.form[key] = this.toDateInputValue(p[key]);
          } else if (p[key] !== undefined && p[key] !== null) {
            // Si el valor existe en la respuesta, usarlo
            this.form[key] = p[key];
          } else {
            // Si es null/undefined, mantener el tipo según el valor inicial del form
            if (typeof this.form[key] === "boolean") {
              this.form[key] = false;
            } else if (typeof this.form[key] === "number") {
              this.form[key] = null;
            } else {
              this.form[key] = "";
            }
          }
        });

        if (this.form.retailerName) {
          const found = this.retailers.find(r => 
            r.internalKey?.toLowerCase() === this.form.retailerName.toLowerCase()
          );
          if (found) this.selectedRetailer = found;
        }
      } catch (e) {
        this.errorMsg = "No se pudo cargar la promoción.";
        console.log(e);
      }
    },
    buildPayload() {
      const payload = {
        ...this.form,
        retailerId: this.selectedRetailer ? this.selectedRetailer.id : null,
        startAt: this.fromDateStart(this.form.startAt),
        endAt: this.fromDateEnd(this.form.endAt),
      };

      //if (payload.couponType !== "code") payload.couponCode = null;
      if (payload.promotionType !== "percentage") payload.discountPercent = null;
      if (payload.promotionType !== "fixed_amount") payload.discountAmount = null;
      if (payload.promotionType !== "free_gift") payload.freeGiftDescription = null;
      if (payload.promotionType !== "shipping") payload.shippingDescription = null;

      if (payload.minOrderValue === "") payload.minOrderValue = null;
      if (payload.minQuantity === "") payload.minQuantity = null;
      if (payload.appliesToListTypes === "") payload.appliesToListTypes = "any";

      if (payload.couponCode) {
        payload.couponType = 'code';
      }
      else {
        payload.couponType = 'none';
        payload.couponCode = null;
      }

      return payload;
    },
    async save() {
      this.errorMsg = null;
      this.touch("title");
      this.touch("marketCountryIso2");
      this.touch("retailerName");

      if (this.disabled) return;

      const start = this.form.startAt ? new Date(this.fromDateStart(this.form.startAt)) : null;
      const end = this.form.endAt ? new Date(this.fromDateEnd(this.form.endAt)) : null;

      if (start && end && end < start) {
        this.errorMsg = "La fecha de fin no puede ser anterior a la de inicio.";
        return;
      }

      this.saving = true;

      try {
        const payload = this.buildPayload();

        if (this.isEdit) {
          const id = this.$route.params.id;
          await axios.put(`${API_BASE}/admin/affiliate-promotions/${id}`, payload, { 
            headers: { Authorization: `Bearer ${this.auth_token}` } 
          });
        } else {
          await axios.post(`${API_BASE}/admin/affiliate-promotions`, payload, { 
            headers: { Authorization: `Bearer ${this.auth_token}` } 
          });
        }

        await this.$router.back();
      } catch (e) {
        this.errorMsg = "No se pudo guardar la promoción.";
        console.log(e);
      } finally {
        this.saving = false;
      }
    },
  },
  async mounted() {
    await this.loadRetailers();
    await this.load();
    this.loaded = true;
  },
};
</script>

<style scoped>
.retailer-selector {
  margin-top: 0.5rem;
}

.retailer-selector__found,
.retailer-selector__not-found {
  background: #f8f9fa;
  border: 1px solid #dee2e6;
  border-radius: 4px;
  padding: 1rem;
}

.retailer-selector__status {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.75rem;
  font-size: 0.875rem;
  font-weight: 500;
}

.retailer-selector__status--warning {
  color: #856404;
}

.retailer-selector__icon {
  font-size: 1rem;
}

.retailer-selector__info {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 0.75rem;
}

.retailer-selector__logo {
  width: 40px;
  height: 40px;
  background: #fff;
  border: 1px solid #dee2e6;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  font-size: 0.875rem;
  color: #495057;
  flex-shrink: 0;
  overflow: hidden;
}

.retailer-selector__logo-img {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.retailer-selector__details {
  flex: 1;
}

.retailer-selector__name {
  font-weight: 500;
  margin-bottom: 0.25rem;
}

.retailer-selector__key {
  font-size: 0.875rem;
  color: #6c757d;
}

.retailer-selector__suggested {
  font-size: 0.875rem;
  margin-bottom: 0.5rem;
}

.retailer-selector__actions {
  display: flex;
  gap: 0.5rem;
}

.create-retailer-modal {
  padding: 8px;
}
</style>
